import type { ScenePersona, ScenePersonaSlot, SceneSettingStep } from '../types';

export interface PersonaManagerState {
  firstPersona: ScenePersona | null;
  secondPersona: ScenePersona | null;
  pendingPersona: ScenePersona | null;
  step: SceneSettingStep;
  activePersonaSlot: ScenePersonaSlot | null;
}

type MinimalPersona = Pick<ScenePersona, 'nickname' | 'dataUrl'>;

export type PersonaManagerAction =
  | { type: 'CLEAR_PENDING' }
  | { type: 'RESET_PERSONAS' }
  | {
      type: 'COMMIT_PENDING_AND_ADVANCE';
      payload: { persona: ScenePersona; slot: ScenePersonaSlot };
    }
  | { type: 'SET_PENDING'; payload: { persona: ScenePersona } }
  | { type: 'SET_PERSONA_DATA_URL'; payload: { slot: ScenePersonaSlot; dataUrl: string } }
  | { type: 'SET_STEP'; payload: SceneSettingStep }
  | { type: 'SET_ACTIVE_PERSONA_SLOT'; payload: ScenePersonaSlot | null }
  | { type: 'CANCEL_PERSONA_FLOW'; payload: SceneSettingStep }
  | { type: 'START_USER_FLOW'; payload: { slot: ScenePersonaSlot; userNickname: string } }
  | { type: 'START_CELEB_FLOW'; payload: { slot: ScenePersonaSlot; persona: MinimalPersona } }
  | { type: 'SELECT_USER_PERSONA'; payload: { persona: MinimalPersona } }
  | { type: 'SELECT_CELEB_PERSONA'; payload: { persona: MinimalPersona } }
  | { type: 'GO_TO_SELECT' };

const initialState: PersonaManagerState = {
  firstPersona: null,
  secondPersona: null,
  pendingPersona: null,
  step: 'bridge',
  activePersonaSlot: null
};

export const personaManagerReducer = (
  state: PersonaManagerState,
  action: PersonaManagerAction
): PersonaManagerState => {
  switch (action.type) {
    case 'CLEAR_PENDING':
      return { ...state, pendingPersona: null };

    case 'RESET_PERSONAS':
      return { ...initialState, step: 'firstPersonaSelected' };

    case 'COMMIT_PENDING_AND_ADVANCE': {
      const { persona, slot } = action.payload;
      const nextStep: SceneSettingStep = slot === 'first' ? 'secondPersonaInput' : 'select';
      return {
        ...state,
        firstPersona: slot === 'first' ? persona : state.firstPersona,
        secondPersona: slot === 'second' ? persona : state.secondPersona,
        pendingPersona: null,
        activePersonaSlot: null,
        step: nextStep
      };
    }

    case 'SET_PENDING':
      return { ...state, pendingPersona: action.payload.persona };

    case 'SET_PERSONA_DATA_URL': {
      const { slot, dataUrl } = action.payload;
      const updater = (prev: ScenePersona | null) => (prev ? { ...prev, dataUrl } : null);
      return {
        ...state,
        firstPersona: slot === 'first' ? updater(state.firstPersona) : state.firstPersona,
        secondPersona: slot === 'second' ? updater(state.secondPersona) : state.secondPersona
      };
    }

    case 'SET_STEP':
      return { ...state, step: action.payload };

    case 'SET_ACTIVE_PERSONA_SLOT':
      return { ...state, activePersonaSlot: action.payload };

    case 'CANCEL_PERSONA_FLOW':
      return {
        ...state,
        pendingPersona: null,
        activePersonaSlot: null,
        step: action.payload
      };

    case 'START_USER_FLOW': {
      const { slot, userNickname } = action.payload;
      return {
        ...state,
        activePersonaSlot: slot,
        pendingPersona: {
          nickname: userNickname,
          dataUrl: '',
          entityType: 'User'
        },
        step: 'enterUserInfo'
      };
    }

    case 'START_CELEB_FLOW': {
      const { slot, persona } = action.payload;
      return {
        ...state,
        activePersonaSlot: slot,
        pendingPersona: {
          nickname: persona.nickname,
          dataUrl: persona.dataUrl,
          entityType: 'Character'
        },
        step: 'enterArtistInfo'
      };
    }

    case 'SELECT_USER_PERSONA': {
      const { persona } = action.payload;
      return {
        ...state,
        pendingPersona: {
          nickname: persona.nickname,
          dataUrl: persona.dataUrl,
          entityType: 'User'
        },
        step: 'enterUserInfo'
      };
    }

    case 'SELECT_CELEB_PERSONA': {
      const { persona } = action.payload;
      return {
        ...state,
        pendingPersona: {
          nickname: persona.nickname,
          dataUrl: persona.dataUrl,
          entityType: 'Character'
        },
        step: 'enterArtistInfo'
      };
    }

    case 'GO_TO_SELECT':
      return { ...state, step: 'select' };

    default:
      return state;
  }
};

export const initialPersonaManagerState = initialState;
